/**
 * Browser bundle entry (FLASH-30). esbuild bundles this (with ts-fsrs and the
 * shared fold) to app/public/scripts/dist/flash-sync.js as an IIFE exposing
 * `window.FlashSync` — the static pages stay bundler-free and pull the engine
 * in with one script tag.
 *
 *   npm run build:client
 */
import {
  SyncEngine,
  MemorySyncStore,
  defaultClientParams,
  MAX_PUSH_BATCH,
  type PushTransport,
  type SyncStore,
  type StoredReviewEvent,
} from "./sync-engine";
import { IndexedDbSyncStore } from "./indexeddb-store";

export {
  SyncEngine,
  MemorySyncStore,
  IndexedDbSyncStore,
  defaultClientParams,
  MAX_PUSH_BATCH,
};
export type { PushTransport, SyncStore, StoredReviewEvent };

/** One-call browser setup: IndexedDB store (memory fallback) + engine. */
export async function createEngine(): Promise<SyncEngine> {
  let store: SyncStore;
  try {
    store = await IndexedDbSyncStore.open();
  } catch {
    // Private-mode or storage-denied browsers: study still works, durability
    // degrades to the session (the outbox lives only in memory).
    store = new MemorySyncStore();
  }
  return new SyncEngine(store);
}

/** Transport against the real API for a same-origin session-cookie page. */
export function fetchTransport(baseUrl = ""): PushTransport {
  return async (body, idempotencyKey) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    };
    const browserGlobal = globalThis as unknown as {
      localStorage?: { getItem(key: string): string | null };
      sessionStorage?: { getItem(key: string): string | null };
    };
    const apiKey =
      browserGlobal.localStorage?.getItem("flash-api-key") ??
      browserGlobal.sessionStorage?.getItem("flash-api-key") ??
      "";
    if (apiKey) {
      headers.Authorization = apiKey.startsWith("Bearer ") ? apiKey : `Bearer ${apiKey}`;
    }

    // credentials is a browser-only RequestInit field; the repo typechecks
    // under workers-types (no DOM lib), so the init is cast once here.
    const init = {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(body),
    } as unknown as RequestInit;
    const res = await fetch(`${baseUrl}/v1/study/reviews`, init);
    if (!res.ok) throw new Error(`push failed: ${res.status}`);
    return res.json();
  };
}
